import type { ApolloError } from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';

import { EDIT, DELETE, REJECT } from 'src/consts';
import { isGraphQLAuthError } from 'src/modules/error-handling/error-handling';
import type { ActionName } from 'src/types';
import type * as Schema from 'src/types/graphql';

import { type EducationAction, Actions } from './actions';

export const updateQualification = (
  updateQualificationInput: Schema.UpdateQualificationInput,
  action: ActionName,
): EducationAction => ({
  type: Actions.UPDATE_QUALIFICATION_START,
  payload: { updateQualificationInput, action },
  actionName: action,
  meta: {
    metrics: {
      action,
    },
  },
});

export const updateQualificationSuccess = (): EducationAction => ({
  type: Actions.UPDATE_QUALIFICATION_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const updateQualificationFailure = (
  error: ApolloError,
): EducationAction => ({
  type: Actions.UPDATE_QUALIFICATION_FAILURE,
  payload: { error },
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const removeQualification = (): EducationAction => ({
  type: Actions.REMOVE_QUALIFICATION_START,
  actionName: DELETE,
});

export const removeQualificationSuccess = (): EducationAction => ({
  type: Actions.REMOVE_QUALIFICATION_SUCCESS,
  meta: {
    profileUpdate: true,
    metrics: {
      'profile-update': true,
    },
  },
});

export const removeQualificationFailure = (
  error: ApolloError,
): EducationAction => ({
  type: Actions.REMOVE_QUALIFICATION_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const fetchConfirmedQualificationSuccess = (
  qualifications: Education[],
): EducationAction => ({
  type: Actions.FETCH_CONFIRMED_QUALIFICATIONS_SUCCESS,
  payload: qualifications,
});

export const updateVerifiedQualification = (): EducationAction => ({
  type: Actions.UPDATE_VERIFIED_QUALIFICATION_START,
  actionName: EDIT,
});

export const updateVerifiedQualificationSuccess = (): EducationAction => ({
  type: Actions.UPDATE_VERIFIED_QUALIFICATION_SUCCESS,
  meta: {
    profileUpdate: true,
    metrics: {
      'profile-update': true,
    },
  },
});

export const updateVerifiedQualificationFailure = (
  error: ApolloError,
): EducationAction => ({
  type: Actions.UPDATE_VERIFIED_QUALIFICATION_FAILURE,
  payload: { error },
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const addUnconfirmedQualification = (
  id: { id: string },
  action: string,
  contentRead = true,
): EducationAction => ({
  type: Actions.ADD_UNCONFIRMED_QUALIFICATION_START,
  payload: id,
  actionName: action,
  meta: {
    metrics: {
      action,
    },
    analytics: {
      contentRead,
    },
  },
});

export const trackUnconfirmedQualificationImpression = (
  qualification: Education,
) => ({
  type: Actions.UNCONFIRMED_QUALIFICATION_IMPRESSION,
  payload: qualification,
});

export const addUnconfirmedQualificationSuccess = (): EducationAction => ({
  type: Actions.ADD_UNCONFIRMED_QUALIFICATION_SUCCESS,
  meta: {
    profileUpdate: true,
  },
});

export const addUnconfirmedQualificationFailure = (
  error: ApolloError,
): EducationAction => ({
  type: Actions.ADD_UNCONFIRMED_QUALIFICATION_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const rejectUnconfirmedQualification = (payload: {
  id: string;
}): EducationAction => ({
  type: Actions.REJECT_UNCONFIRMED_QUALIFICATION_START,
  payload,
  actionName: REJECT,
});

export const rejectUnconfirmedQualificationSuccess = (): EducationAction => ({
  type: Actions.REJECT_UNCONFIRMED_QUALIFICATION_SUCCESS,
  meta: {
    metrics: {
      'profile-update': true,
    },
  },
});

export const rejectUnconfirmedQualificationFailure = (
  error: ApolloError,
): EducationAction => ({
  type: Actions.REJECT_UNCONFIRMED_QUALIFICATION_FAILURE,
  payload: error,
  meta: {
    metrics: {
      error: !isGraphQLAuthError(error),
    },
  },
});

export const fetchUnconfirmedQualificationsSuccess = (
  unconfirmedQualifications: Education[],
): EducationAction => ({
  type: Actions.FETCH_UNCONFIRMED_QUALIFICATIONS_SUCCESS,
  payload: unconfirmedQualifications,
});
